import React, { FC } from "react";
import styled from "styled-components";
import * as Yup from "yup";
import { useInView } from "react-intersection-observer";
import { Image, colors, font } from "@literati/public-ui-shared-react";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import { SubscriptionPlanCamelCase } from "lib/api/subscription";
import { StepHeaderClassName } from "components/Step/StepHeader";
import { useSelectPlan } from "components/PlanSelection/useSelectPlan";
import { FormikSubmitStep } from "components/Step/stepTypes";
import {
  SelectPlanKey as Key,
  PlanPrepaid,
  SelectPlanValues,
} from "components/PlanSelection/planSelectionTypes";
import PlanSelectionPrepaidPromo from "components/PlanSelection/PlanSelectionPrepaidPromo";
import PlanSelectionPrepaidDetailsCollapsible from "components/PlanSelection/PlanSelectionPrepaidDetailsCollapsible";
import PlanSelectionMessageAutoRenew from "components/PlanSelection/PlanSelectionMessageAutoRenew";
import { getPlanPrepaidPricing } from "components/PlanSelection/planSelectionUtil";

export const VALIDATION_SCHEMA = Yup.object().shape({
  [Key.PLAN_SLUG]: Yup.string().required(),
});

export const INITIAL_VALUES: SelectPlanValues = {
  [Key.KIND]: "select-plan",
  [Key.PLAN_SLUG]: "",
} as const;

const StyledStepLayout = styled(StepLayout)`
  .${StepHeaderClassName.BASE} {
    box-shadow: 0px 0px 5px ${colors.newsprint1};
  }

  .${StepLayoutClassName.CONTENT_SPACING} {
    width: 100vw;
  }

  .${StepLayoutClassName.FORM} {
    max-width: none;
    display: flex;
    flex-direction: column;
  }

  .guided-sign-up-radio-label {
    font-weight: 700;
  }
`;

const StyledImage = styled(Image)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0 0;

  img {
    /* offset to make book box image look centered with other content (due to shadow in image) */
    /* @todo: remove if provided new asset */
    margin-left: 1rem !important;
  }

  background-image: linear-gradient(
    to bottom,
    ${colors.periwinkleShade1} 71%,
    white 71%,
    white 100%
  );
`;

const ConstrainedWidthContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0.25rem 0 0;
  padding: 0 0 1.5rem;
  align-self: center;
  max-width: 335px;

  > * {
    width: 100%;
  }
`;

const StyledH1 = styled.h1`
  text-align: center;
  font-family: ${font.fontFamily.hoefler};
  font-size: 1.375rem;
`;

const StyledPlanSelectionPrepaidPromo = styled(PlanSelectionPrepaidPromo)`
  margin: 1rem 0 0;
  justify-content: center;
`;

const StickyPromoContainer = styled.div`
  position: sticky;
  top: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 6rem;
  margin: auto;
  background-color: white;
  z-index: 2;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.12);
`;

const StyledPlanSelectionMessageAutoRenew = styled(
  PlanSelectionMessageAutoRenew
)`
  margin: 1.5rem 0 0;
  font-size: 0.75rem;
`;

export type StepLayoutPropsSelectPlanPrepaid = Omit<
  StepLayoutProps<SelectPlanValues>,
  "asForm" | "children" | "formId"
>;

export interface StepSelectPlanPrepaidProps
  extends StepLayoutPropsSelectPlanPrepaid {
  onSubmit?: FormikSubmitStep<SelectPlanValues>;
  plansPrepaid?: PlanPrepaid[] | null;
  selectedPlan?: SubscriptionPlanCamelCase | null;
}

export const StepSelectPlanPrepaid = ({
  onSubmit,
  plansPrepaid,
  selectedPlan,
  ...props
}: StepSelectPlanPrepaidProps) => {
  const { handleSubmit } = useSelectPlan({ onSubmit });

  const { ref, inView: isPromoInView } = useInView({
    initialInView: true,
    threshold: 0,
    rootMargin: "-140px 0px",
  });

  return (
    <StyledStepLayout<FC<StepLayoutProps<SelectPlanValues>>>
      asForm
      bgColor="white"
      disableContentSpacing
      formId="step-select-prepaid-plan"
      formikProps={{
        initialValues: {
          ...INITIAL_VALUES,
          [Key.PLAN_SLUG]: selectedPlan?.slug || INITIAL_VALUES[Key.PLAN_SLUG],
        },
        onSubmit: handleSubmit,
        validationSchema: VALIDATION_SCHEMA,
      }}
      {...props}
    >
      {({ values }) => {
        const plan = plansPrepaid?.find(
          (p) => p.slug === values[Key.PLAN_SLUG]
        ) as PlanPrepaid;

        const planPrepaidPricing = getPlanPrepaidPricing({
          plan,
          plansPrepaid,
        });

        return (
          <>
            {!isPromoInView && plan ? (
              <StickyPromoContainer>
                <StyledPlanSelectionPrepaidPromo
                  css="margin: 0"
                  plan={plan}
                  planPrepaidPricing={planPrepaidPricing}
                />
              </StickyPromoContainer>
            ) : null}
            <StyledImage
              alt="prepaid book box"
              contain
              src="https://siren-images.literati.com/images/Prepaid_3_book_box_light.original.png"
              height={147}
              width={271}
              targetWidth={263}
            />
            <ConstrainedWidthContent>
              <StyledH1>Book Club Membership</StyledH1>
              {plan ? (
                <div ref={ref}>
                  <StyledPlanSelectionPrepaidPromo
                    plan={plan}
                    planPrepaidPricing={planPrepaidPricing}
                  />
                </div>
              ) : null}
              <div css="margin: 1rem 0 0">
                <PlanSelectionPrepaidDetailsCollapsible />
              </div>
              <StyledPlanSelectionMessageAutoRenew />
            </ConstrainedWidthContent>
          </>
        );
      }}
    </StyledStepLayout>
  );
};

export default StepSelectPlanPrepaid;
